import * as Sentry from '@sentry/react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || '';
const ENVIRONMENT =
  process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development';

let initialized = false;

/**
 * Initialize Sentry error reporting, session replay and structured logs.
 * Call once at app startup, before anything else runs, so early boot errors
 * are captured too.
 *
 * Replay masks all text and blocks media by default: Flashtools screens show
 * ticket barcodes, buyer emails and card details, none of which should leave
 * the browser. Do not turn masking off.
 */
export function initSentry() {
  if (initialized || !SENTRY_DSN) {
    if (!SENTRY_DSN) {
      console.warn('[Sentry] Missing REACT_APP_SENTRY_DSN — skipping init');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,

    // Structured logs (Sentry.logger.*) plus anything the app already sends to
    // console.warn / console.error.
    enableLogs: true,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],

    // Performance. Sample hard in prod, everything locally.
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
    // Same-origin only. Adding the API host here makes Sentry attach
    // `sentry-trace` and `baggage` headers to every cross-origin request,
    // which the browser then preflights — and any header the API does not list
    // in Access-Control-Allow-Headers fails the preflight and kills the
    // request outright. That took every API call down in production once.
    //
    // Do not add the API host back until oneClickTixCore both runs a Sentry
    // SDK (so a linked trace is worth something) and lists sentry-trace and
    // baggage in CORS_ALLOW_HEADERS.
    tracePropagationTargets: [/^\//],

    // Session Replay: 10% of all sessions, 100% of sessions that hit an error.
    replaysSessionSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Never ship request/response bodies or default PII — Flashtools payloads
    // carry Firebase ids, emails and ticket data.
    sendDefaultPii: false,

    ignoreErrors: [
      // Browser extensions and cancelled navigations, not our bugs.
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^AbortError/,
    ],
  });

  Sentry.setTag('app_source', 'flashtools');

  initialized = true;
}

/**
 * Attach the current user to every event and replay.
 * Call on login / auth state change, mirroring analytics identifyUser().
 */
export function identifySentryUser(userId: string, email?: string) {
  if (!initialized) return;
  Sentry.setUser({ id: userId, email: email || undefined });
}

/**
 * Clear user identity on logout.
 */
export function resetSentryUser() {
  if (!initialized) return;
  Sentry.setUser(null);
}

/**
 * Report a handled error with context.
 *
 * Usage:
 *   captureError(err, { where: 'transferTickets', platform: 'dice' });
 */
export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (!initialized) {
    console.error('[Sentry] (not initialized)', error, context);
    return;
  }
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/**
 * Structured log line. Shows up in Sentry Logs, and as a breadcrumb on any
 * error that follows it.
 *
 * Usage:
 *   logInfo('Transfer started', { ticketCount: 3 });
 */
export function logInfo(message: string, attributes?: Record<string, unknown>) {
  if (!initialized) return;
  Sentry.logger.info(message, attributes);
}

export function logWarn(message: string, attributes?: Record<string, unknown>) {
  if (!initialized) return;
  Sentry.logger.warn(message, attributes);
}

export function logError(message: string, attributes?: Record<string, unknown>) {
  if (!initialized) return;
  Sentry.logger.error(message, attributes);
}

/**
 * Leave a trail without reporting anything. Breadcrumbs are attached to
 * whatever error comes next and are visible alongside the session replay, so
 * this is where "what was the user doing" belongs — not captureError().
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: 'info' | 'warning' | 'error' = 'info',
) {
  if (!initialized) return;
  Sentry.addBreadcrumb({ category, message, data, level });
}

/**
 * Tag the session so issues can be filtered by it. Use for slow-moving facts
 * (session kind, plan tier), never for per-request values.
 */
export function setSentryTag(key: string, value: string) {
  if (!initialized) return;
  Sentry.setTag(key, value);
}

export { Sentry };
